import type { PropertyEditorTabKey } from '../../propertyEditorTypes';

export function getDirtyBorderClass(args: {
  mode: 'create' | 'edit';
  id?: string | null;
  isTempId?: (id: string) => boolean;

  // dirty map: id -> true/false/undefined
  dirtyById: Record<string, boolean | undefined>;
}) {
  const { mode, id, dirtyById, isTempId } = args;

  if (mode !== 'edit' || !id) return undefined;

  const isDirty = !!dirtyById[id];
  if (isDirty) return 'border-orange-500/70 ring-1 ring-orange-500/30';

  // ✅ show green only if the item is explicitly tracked as clean (id exists in map and false)
  const isTracked = Object.prototype.hasOwnProperty.call(dirtyById, id);
  const isCleanTracked = isTracked && dirtyById[id] === false;

  if (isCleanTracked && !(isTempId?.(id) ?? false)) {
    return 'border-emerald-500/60 ring-1 ring-emerald-500/25';
  }

  return undefined;
}

/**
 * reconcileKeepOrder
 *
 * Reconciles a previously ordered list with a new source list **without re-sorting**.
 *
 * - Preserves the current UI order (important while editing items).
 * - Updates items with their latest versions from the source list.
 * - Inserts newly added items (not previously present) at the top or bottom.
 *
 * This is used to prevent items from "jumping" when the underlying data changes
 * (e.g. typing in a title while a sort is active).
 *
 * Typical flow:
 * 1) User explicitly sorts → apply a real sort once.
 * 2) Subsequent edits → call reconcileKeepOrder to keep visual order stable.
 *
 * @param currentOrdered - The list as currently displayed in the UI
 * @param nextSource - The latest source list (e.g. from store or backend)
 * @param opts.insertNew - Where to place newly discovered items ('top' | 'bottom'), default 'top'
 * @param opts.includeNoIdFromSource - Whether to include items without IDs from source, default true
 *
 * @returns A new array with stable order and updated item references
 */
export function reconcileKeepOrder<T extends { id?: string }>(
  prev: T[],
  next: T[],
  opts?: { insertNew?: 'top' | 'bottom' },
): T[] {
  const insertNew = opts?.insertNew ?? 'bottom';

  const nextById = new Map<string, T>();
  for (const it of next) {
    if (it?.id) nextById.set(it.id, it);
  }

  const used = new Set<string>();
  const kept: T[] = [];

  // 1) Keep existing order, but refresh objects from `next`
  for (const old of prev) {
    const id = old?.id;
    if (!id) continue;

    const fresh = nextById.get(id);
    if (!fresh) continue; // ✅ drop anything not in `next` (this removes temp rows)
    kept.push(fresh);
    used.add(id);
  }

  // 2) Find new items not already used
  const added: T[] = [];
  for (const it of next) {
    const id = it?.id;
    if (!id) continue;
    if (used.has(id)) continue;
    added.push(it);
  }

  return insertNew === 'top' ? [...added, ...kept] : [...kept, ...added];
}

export function isTempId(id?: string | null): boolean {
  return typeof id === 'string' && id.startsWith('tmp-');
}

export function buildPropertyEditorPersistKey(args: { propertyID?: string; tab: PropertyEditorTabKey }) {
  const id = args.propertyID?.trim() ? args.propertyID : 'new';
  return `property-editor:${id}:${args.tab}`;
}

export function removeAtIndex<T>(arr: readonly T[] | undefined, idx: number): T[] {
  if (!arr) return [];
  if (idx < 0 || idx >= arr.length) return [...arr];
  return arr.filter((_, i) => i !== idx);
}
